import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
  Separator,
  useToast,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@lead-magnifier/ui'
import { User, Bell, Lock, Trash2, Save } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/settings/')({
  component: SettingsPage,
})

function SettingsPage() {
  const { user, signOut } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Profile state
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  // Notification state
  const [leadNotifications, setLeadNotifications] = useState(true)
  const [weeklyReports, setWeeklyReports] = useState(true)

  // Password state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!user,
  })

  // Load profile data
  useEffect(() => {
    if (profile) {
      setName(profile.name || '')
      setEmail(profile.email || '')
      setLeadNotifications(profile.lead_notifications_enabled ?? true)
      setWeeklyReports(profile.weekly_reports_enabled ?? true)
    } else if (user) {
      setName(user.user_metadata?.name || '')
      setEmail(user.email || '')
    }
  }, [profile, user])

  // Update profile mutation
  const updateProfile = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Nicht angemeldet')

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: { name },
      })
      if (authError) throw authError

      // Update profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          name,
          lead_notifications_enabled: leadNotifications,
          weekly_reports_enabled: weeklyReports,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id)

      if (profileError) throw profileError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      toast({
        title: 'Gespeichert',
        description: 'Deine Einstellungen wurden aktualisiert.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Speichern fehlgeschlagen',
        variant: 'destructive',
      })
    },
  })

  // Update password mutation
  const updatePassword = useMutation({
    mutationFn: async () => {
      if (newPassword !== confirmPassword) {
        throw new Error('Passwörter stimmen nicht überein')
      }
      if (newPassword.length < 6) {
        throw new Error('Passwort muss mindestens 6 Zeichen lang sein')
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error
    },
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      toast({
        title: 'Passwort geändert',
        description: 'Dein Passwort wurde erfolgreich aktualisiert.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Passwort ändern fehlgeschlagen',
        variant: 'destructive',
      })
    },
  })

  // Delete account mutation
  const deleteAccount = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Nicht angemeldet')

      // Delete user profile
      await supabase.from('user_profiles').delete().eq('id', user.id)

      // Delete from tenant_users
      await supabase.from('tenant_users').delete().eq('user_id', user.id)

      // Note: Leads and Events remain, but are orphaned
      // In a real app, you'd handle this differently

      // Sign out
      await signOut()
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Konto löschen fehlgeschlagen',
        variant: 'destructive',
      })
    },
  })

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold">Einstellungen</h1>
        <p className="text-muted-foreground mt-1">
          Verwalte dein Profil und Benachrichtigungen
        </p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profil
          </CardTitle>
          <CardDescription>Deine persönlichen Informationen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              type="email"
              value={email}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              E-Mail-Adresse kann nicht geändert werden
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Benachrichtigungen
          </CardTitle>
          <CardDescription>Wähle, welche E-Mails du erhalten möchtest</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Lead-Benachrichtigungen</Label>
              <p className="text-sm text-muted-foreground">
                E-Mail bei jedem neuen bestätigten Lead
              </p>
            </div>
            <Switch
              checked={leadNotifications}
              onCheckedChange={setLeadNotifications}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Wöchentlicher Report</Label>
              <p className="text-sm text-muted-foreground">
                Zusammenfassung deiner Leads jede Woche
              </p>
            </div>
            <Switch
              checked={weeklyReports}
              onCheckedChange={setWeeklyReports}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button
        onClick={() => updateProfile.mutate()}
        disabled={updateProfile.isPending}
        className="w-full sm:w-auto"
      >
        <Save className="h-4 w-4 mr-2" />
        {updateProfile.isPending ? 'Speichert...' : 'Änderungen speichern'}
      </Button>

      <Separator />

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Passwort ändern
          </CardTitle>
          <CardDescription>Aktualisiere dein Passwort</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">Neues Passwort</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mindestens 6 Zeichen"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <Button
            onClick={() => updatePassword.mutate()}
            disabled={!newPassword || !confirmPassword || updatePassword.isPending}
            variant="outline"
          >
            {updatePassword.isPending ? 'Wird geändert...' : 'Passwort ändern'}
          </Button>
        </CardContent>
      </Card>

      <Separator />

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Gefahrenzone
          </CardTitle>
          <CardDescription>Unwiderrufliche Aktionen</CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                Konto löschen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Konto wirklich löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Diese Aktion kann nicht rückgängig gemacht werden. Dein Konto und alle
                  damit verbundenen Daten werden dauerhaft gelöscht.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteAccount.mutate()}
                  className="bg-destructive text-destructive-foreground"
                >
                  Ja, Konto löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  )
}
